import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';

export function RefundPolicy() {
  return (
    <>
      <SimpleSEO
        title="Refund Policy | MdbApp"
        description="MdbApp Refund Policy - Learn about our refund process for blockchain transactions and service fees"
        keywords="refund policy, MdbApp refunds, blockchain transactions"
      />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Refund Policy</h1>
        <p className="text-muted-foreground mb-8">Last Updated: October 16, 2025</p>

        <div className="space-y-8 text-foreground">
          <section className="bg-yellow-500/10 p-6 rounded-lg border border-yellow-500/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mt-1 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-semibold mb-2 text-yellow-600">Important Notice</h2>
                <p className="text-foreground">
                  Due to the immutable nature of blockchain technology, all transactions processed through MdbApp are <strong>final and non-refundable</strong> once confirmed on the blockchain. Please read this policy carefully before using our services.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">General Refund Policy</h2>
            <p className="mb-4">
              MdbApp operates on blockchain technology where transactions are permanent and irreversible. Once a transaction is confirmed on the blockchain (Ethereum, BNB Smart Chain, or Avalanche), it cannot be reversed, canceled, or refunded.
            </p>
            <p>
              By using our services, you acknowledge and accept that:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>All service fees are non-refundable</li>
              <li>All blockchain gas fees are non-refundable</li>
              <li>Token deployments are permanent and cannot be undone</li>
              <li>You are responsible for verifying all transaction details before confirmation</li>
            </ul>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-6">Refund Eligibility</h2>
            
            <div className="space-y-6">
              <div className="p-6 rounded-xl border border-red-500/20 bg-red-500/5">
                <div className="flex items-start gap-3 mb-3">
                  <XCircle className="w-6 h-6 text-red-500 mt-1 flex-shrink-0" />
                  <h3 className="text-xl font-semibold">Non-Refundable Situations</h3>
                </div>
                <p className="mb-3">Refunds will <strong>NOT</strong> be issued for:</p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>Successfully completed transactions on the blockchain</li>
                  <li>User error in entering transaction details (wrong address, amount, etc.)</li>
                  <li>Change of mind after transaction confirmation</li>
                  <li>Market volatility or token value changes</li>
                  <li>Gas fees paid to the blockchain network</li>
                  <li>Service fees for completed token deployments</li>
                  <li>Tokens that don't perform as expected in the market</li>
                  <li>User dissatisfaction with token features after deployment</li>
                  <li>Regulatory changes affecting your token</li>
                  <li>Third-party service issues (wallet providers, exchanges, etc.)</li>
                </ul>
              </div>

              <div className="p-6 rounded-xl border border-green-500/20 bg-green-500/5">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                  <h3 className="text-xl font-semibold">Refundable Situations</h3>
                </div>
                <p className="mb-3">Refunds <strong>MAY</strong> be issued only if:</p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>A verifiable error occurred on the blockchain that prevented your transaction from completing</li>
                  <li>MdbApp's smart contract failed to execute as intended due to a bug in our code</li>
                  <li>You were charged multiple times for the same service due to a system error</li>
                  <li>MdbApp's service was unavailable and prevented you from completing your transaction after payment</li>
                </ul>
                <p className="mt-4 text-sm">
                  <strong>Note:</strong> All refund claims must be supported by verifiable blockchain evidence (transaction hashes, block explorer links, etc.)
                </p>
              </div>
            </div>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Refund Process</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-[#0A74FF]/10 mt-1">
                  <span className="text-[#0A74FF] font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Submit a Refund Request</h3>
                  <p className="text-muted-foreground">
                    Contact us via Twitter <a href="https://x.com/intent/follow?screen_name=mobdaboss" target="_blank" rel="noopener noreferrer" className="text-[#0A74FF] hover:underline">@MobDaBoss</a> with:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-muted-foreground">
                    <li>Your wallet address</li>
                    <li>Transaction hash(es)</li>
                    <li>Detailed description of the issue</li>
                    <li>Screenshots or blockchain explorer links as evidence</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-[#0A74FF]/10 mt-1">
                  <span className="text-[#0A74FF] font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Investigation</h3>
                  <p className="text-muted-foreground">
                    Our team will investigate your claim by:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-muted-foreground">
                    <li>Verifying the transaction on the blockchain</li>
                    <li>Reviewing our system logs</li>
                    <li>Analyzing the smart contract execution</li>
                    <li>Determining if the issue was caused by MdbApp or external factors</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-[#0A74FF]/10 mt-1">
                  <span className="text-[#0A74FF] font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Decision & Processing</h3>
                  <p className="text-muted-foreground">
                    If your refund is approved:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-muted-foreground">
                    <li>You will be notified via Twitter DM or your provided contact method</li>
                    <li>The refund will be automatically processed within <strong>7 business days</strong></li>
                    <li>Refunds are issued to the original wallet address used for the transaction</li>
                    <li>You will receive a confirmation transaction hash for the refund</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-6 h-6 text-[#0A74FF]" />
              Refund Timeline
            </h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-32 font-semibold">Investigation:</div>
                <div className="text-muted-foreground">1-3 business days</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 font-semibold">Decision:</div>
                <div className="text-muted-foreground">1-2 business days</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 font-semibold">Processing:</div>
                <div className="text-muted-foreground">Up to 7 business days from approval</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-32 font-semibold">Total:</div>
                <div className="text-muted-foreground font-semibold">Maximum 12 business days</div>
              </div>
            </div>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Blockchain Gas Fees</h2>
            <p className="mb-4">
              <strong>Important:</strong> Gas fees paid to blockchain networks (Ethereum, BSC, Avalanche) are <strong>never refundable</strong> under any circumstances.
            </p>
            <p className="text-muted-foreground">
              Gas fees are paid directly to network validators and miners, not to MdbApp. These fees are required to process transactions on the blockchain and are outside of our control. Even if a transaction fails, gas fees are consumed by the network and cannot be recovered.
            </p>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">User Responsibility</h2>
            <p className="mb-4">
              Before confirming any transaction, you are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Carefully reviewing all transaction details</li>
              <li>Verifying wallet addresses and amounts</li>
              <li>Understanding the token features you're configuring</li>
              <li>Ensuring you have sufficient funds for gas fees</li>
              <li>Reading and understanding our Terms of Service</li>
              <li>Testing with small amounts first if unsure</li>
              <li>Consulting with legal/financial advisors as needed</li>
            </ul>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Dispute Resolution</h2>
            <p className="mb-4">
              If you disagree with our refund decision:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
              <li>You may request a review by providing additional evidence</li>
              <li>Our team will conduct a secondary review within 5 business days</li>
              <li>The secondary review decision is final</li>
              <li>For legal disputes, refer to the Governing Law section in our Terms of Service</li>
            </ol>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Changes to This Policy</h2>
            <p className="mb-4">
              We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting. Your continued use of MdbApp after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
            <p className="mb-4">
              For refund requests or questions about this policy:
            </p>
            <ul className="list-none space-y-2">
              <li><strong>Twitter:</strong> <a href="https://x.com/intent/follow?screen_name=mobdaboss" target="_blank" rel="noopener noreferrer" className="text-[#0A74FF] hover:underline">@MobDaBoss</a></li>
              <li><strong>Website:</strong> https://mdbapp.io</li>
            </ul>
          </section>

          <section className="border-t pt-8 bg-blue-500/10 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 text-center">💡 Best Practices to Avoid Issues</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h3 className="font-semibold">Before Transaction:</h3>
                <ul className="list-disc pl-6 text-sm space-y-1 text-muted-foreground">
                  <li>Double-check all addresses</li>
                  <li>Verify token settings</li>
                  <li>Read our guides</li>
                  <li>Test with small amounts</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">During Transaction:</h3>
                <ul className="list-disc pl-6 text-sm space-y-1 text-muted-foreground">
                  <li>Review wallet confirmation</li>
                  <li>Check gas fee estimates</li>
                  <li>Don't rush the process</li>
                  <li>Save transaction hashes</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
